import { state, esc, get, num, usd, scopeQs } from './core'
import { openDetail } from './sessions'
import { mountScopeBar, hasScopeControls } from './filterbar'

interface MixRow { model: string; taskType: string; predictedComplexity: string; costUsd: number; blocks: number; success: string | null }
interface ModelAgg {
  model: string; total: number
  byTask: Array<{ key: string; cost: number }>
  byCx: Array<{ key: string; cost: number }>
}

function aggregateMix(rows: MixRow[]): ModelAgg[] {
  var models = new Map<string, { total: number; tasks: Map<string, number>; cxs: Map<string, number> }>()
  for (var i = 0; i < rows.length; i++) {
    var r = rows[i]
    var m = models.get(r.model)
    if (!m) { m = { total: 0, tasks: new Map(), cxs: new Map() }; models.set(r.model, m) }
    m.total += r.costUsd
    m.tasks.set(r.taskType, (m.tasks.get(r.taskType) || 0) + r.costUsd)
    m.cxs.set(r.predictedComplexity, (m.cxs.get(r.predictedComplexity) || 0) + r.costUsd)
  }
  var out: ModelAgg[] = []
  models.forEach(function (v, k) {
    var byTask: Array<{ key: string; cost: number }> = []
    v.tasks.forEach(function (c, t) { byTask.push({ key: t, cost: c }) })
    byTask.sort(function (a, b) { return b.cost - a.cost })
    var byCx: Array<{ key: string; cost: number }> = []
    v.cxs.forEach(function (c, t) { byCx.push({ key: t, cost: c }) })
    byCx.sort(function (a, b) { return b.cost - a.cost })
    out.push({ model: k, total: v.total, byTask: byTask, byCx: byCx })
  })
  out.sort(function (a, b) { return b.total - a.total })
  return out
}

interface DimModelAgg {
  model: string; cost: number; tasks: number; succeeded: number
}
interface DimGroup {
  key: string; cost: number; tasks: number; succeeded: number
  byModel: DimModelAgg[]
}

function aggregateByDim(rows: MixRow[], dim: 'taskType' | 'predictedComplexity'): DimGroup[] {
  var groups = new Map<string, Map<string, { cost: number; tasks: number; succeeded: number }>>()
  for (var i = 0; i < rows.length; i++) {
    var r = rows[i]
    var k = r[dim]
    var g = groups.get(k)
    if (!g) { g = new Map(); groups.set(k, g) }
    var m = g.get(r.model)
    if (!m) { m = { cost: 0, tasks: 0, succeeded: 0 }; g.set(r.model, m) }
    m.cost += r.costUsd
    m.tasks += r.blocks
    if (r.success === '"success"') m.succeeded += r.blocks
  }
  var out: DimGroup[] = []
  groups.forEach(function (models, key) {
    var cost = 0, tasks = 0, succeeded = 0
    var byModel: DimModelAgg[] = []
    models.forEach(function (v, model) {
      cost += v.cost; tasks += v.tasks; succeeded += v.succeeded
      byModel.push({ model: model, cost: v.cost, tasks: v.tasks, succeeded: v.succeeded })
    })
    byModel.sort(function (a, b) { return b.cost - a.cost })
    out.push({ key: key, cost: cost, tasks: tasks, succeeded: succeeded, byModel: byModel })
  })
  out.sort(function (a, b) { return b.cost - a.cost })
  return out
}

function pct(n: number, d: number): string {
  if (!d) return '—'
  return Math.round((n / d) * 100) + '%'
}

function costPer(cost: number, n: number): string {
  if (!n) return '—'
  return usd(cost / n)
}

var loaded = false
var blocksData: any[] | null = null
var facets: { taskTypes: string[]; complexities: string[]; repos: string[]; models: string[] } | null = null
var lastKey = ''
var mdSearch = ''
var mdDays: string | number = 'all'
var MD_TIME_PRESETS = [
  { d: 7, l: '7d' }, { d: 14, l: '14d' }, { d: 30, l: '30d' }, { d: 90, l: '90d' }, { d: 'all', l: 'All' },
]
var mdTaskType = ''
var mdComplexity = ''
var mdRepo = ''
var mdSuccess = ''
var selectedTask = 0
var selectedCx = 0
var mdModel = ''

function winQs() {
  return 'days=' + encodeURIComponent(mdDays) + scopeQs()
}

function cacheKey() {
  return mdDays + '|' + scopeQs()
}

export function renderModels() {
  var key = cacheKey()
  if (loaded && key === lastKey) return
  lastKey = key
  loaded = true
  blocksData = null
  facets = null

  var el = document.getElementById('models-tab')
  if (!el) return
  el.innerHTML =
    '<div class="md-panel">' +
      '<div class="scope-row" id="md-toolbar">' +
        '<div class="filter-bar" id="md-scope"></div>' +
        '<div class="filter-bar" id="md-time-bar"></div>' +
      '</div>' +
      '<section class="panel">' +
        '<div class="panel-head"><h2>Model Mix</h2></div>' +
        '<div class="md-mix" id="md-mix"><div class="empty">Loading…</div></div>' +
      '</section>' +
      '<section class="panel">' +
        '<div class="panel-head"><h2>Mix by Task Type</h2></div>' +
        '<div class="md-mix" id="md-mix-task"><div class="empty">Loading…</div></div>' +
      '</section>' +
      '<section class="panel">' +
        '<div class="panel-head"><h2>Mix by Complexity</h2></div>' +
        '<div class="md-mix" id="md-mix-cx"><div class="empty">Loading…</div></div>' +
      '</section>' +
      '<section class="panel">' +
        '<div class="panel-head"><h2>Tasks</h2></div>' +
        '<div class="filters md-filters" id="md-filters"></div>' +
        '<div class="md-roster" id="md-roster"><div class="empty">Loading…</div></div>' +
      '</section>' +
    '</div>'

  if (hasScopeControls()) {
    mountScopeBar(document.getElementById('md-scope')!, function () {
      loaded = false
      lastKey = ''
      renderModels()
    })
  }

  paintTimeBar()

  var qs = winQs()
  get('/api/models/task-mix?' + qs).then(function (rows) {
    var mixRows = rows as MixRow[]
    paintMix(mixRows)
    paintDimMix(mixRows, 'taskType', 'md-mix-task', function () { return selectedTask }, function (v) { selectedTask = v })
    paintDimMix(mixRows, 'predictedComplexity', 'md-mix-cx', function () { return selectedCx }, function (v) { selectedCx = v })
  })
  get('/api/models/block-facets?' + qs).then(function (f) {
    facets = f
    paintBlockFilters()
    fetchBlocks()
  })
}

function filterQs(): string {
  var parts: string[] = []
  if (mdTaskType) parts.push('task_type=' + encodeURIComponent(mdTaskType))
  if (mdComplexity) parts.push('complexity=' + encodeURIComponent(mdComplexity))
  if (mdRepo) parts.push('repo=' + encodeURIComponent(mdRepo))
  if (mdSuccess) parts.push('success=' + encodeURIComponent(mdSuccess))
  if (mdSearch) parts.push('q=' + encodeURIComponent(mdSearch))
  if (mdModel) parts.push('model=' + encodeURIComponent(mdModel))
  return parts.length ? '&' + parts.join('&') : ''
}

function fetchBlocks() {
  var host = document.getElementById('md-roster')
  if (host) host.innerHTML = '<div class="empty">Loading…</div>'
  get('/api/models/blocks?' + winQs() + filterQs()).then(function (rows) {
    blocksData = rows as any[]
    paintRoster()
  })
}

function modelShort(m: string): string {
  return m.replace(/^claude-/, '').replace(/-\d{8}$/, '')
}

interface DrillFilter { taskType?: string; predictedComplexity?: string; model?: string }

function drillToBlocks(filter: DrillFilter) {
  mdTaskType = filter.taskType || ''
  mdComplexity = filter.predictedComplexity || ''
  mdModel = filter.model || ''
  paintBlockFilters()
  fetchBlocks()
  var blocks = document.getElementById('md-roster')
  if (blocks) blocks.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

function wireDrillButtons(host: HTMLElement) {
  Array.prototype.forEach.call(host.querySelectorAll('.mm-drill'), function (btn) {
    btn.onclick = function () {
      var filter = JSON.parse(btn.getAttribute('data-drill')!) as DrillFilter
      drillToBlocks(filter)
    }
  })
}

function hbarRows(
  items: Array<{ key: string; cost: number; fullKey?: string }>, limit: number, id: string,
  drillBase?: DrillFilter, drillDim?: 'taskType' | 'predictedComplexity' | 'model',
): string {
  var max = items.length ? items[0].cost : 0
  function rowHtml(r: { key: string; cost: number; fullKey?: string }) {
    var pct = max > 0 ? Math.round((r.cost / max) * 100) : 0
    var btn = ''
    if (drillBase && drillDim) {
      var filter = JSON.parse(JSON.stringify(drillBase))
      filter[drillDim] = r.fullKey || r.key
      btn = '<span class="mm-drill" data-drill=\'' + esc(JSON.stringify(filter)) + '\' title="See sessions">→</span>'
    }
    return '<div class="bar-row"><span class="name">' + esc(r.key) +
      '</span><span class="bar-track"><span class="bar-fill" style="width:' + pct + '%"></span></span>' +
      '<span class="n">' + usd(r.cost) + '</span>' + btn + '</div>'
  }
  var show = items.slice(0, limit)
  var rest = items.slice(limit)
  var html = show.map(rowHtml).join('')
  if (rest.length) {
    html += '<div class="mm-more-rows" id="' + id + '" hidden>' +
      rest.map(rowHtml).join('') + '</div>' +
      '<button class="mm-toggle" data-target="' + id + '">show all (' + items.length + ')</button>'
  }
  return html
}

var selectedModel = 0

function paintMix(rows: MixRow[]) {
  var host = document.getElementById('md-mix')
  if (!host) return
  var models = aggregateMix(rows)
  if (!models.length) { host.innerHTML = '<div class="empty">No model data in this range.</div>'; return }

  var grandTotal = 0
  for (var i = 0; i < models.length; i++) grandTotal += models[i].total

  if (selectedModel >= models.length) selectedModel = 0

  var tiles = ''
  for (var i = 0; i < models.length; i++) {
    var m = models[i]
    var pct = grandTotal > 0 ? Math.round((m.total / grandTotal) * 100) : 0
    tiles += '<div class="tile clickable' + (i === selectedModel ? ' on' : '') + '" data-idx="' + i + '">' +
      '<div class="label">' + esc(modelShort(m.model)) + '</div>' +
      '<div class="value"><span class="num">' + usd(m.total) + '</span></div>' +
      '<div class="sub">' + pct + '% of spend</div>' +
    '</div>'
  }
  var tilesHtml = '<div class="mm-total">' + usd(grandTotal) + ' total spend</div>' +
    '<div class="tiles">' + tiles + '</div>'

  var sel = models[selectedModel]
  var detailHtml = '<div class="mm-detail">' +
    '<div class="mm-detail-head">' + esc(modelShort(sel.model)) + ' breakdown</div>' +
    '<div class="mm-detail-body">' +
      '<div class="mm-breakdown">' +
        '<div class="mm-bd-label">By task type</div>' +
        hbarRows(sel.byTask, 5, 'mm-task-sel', { model: sel.model }, 'taskType') +
      '</div>' +
      '<div class="mm-breakdown">' +
        '<div class="mm-bd-label">By complexity</div>' +
        hbarRows(sel.byCx, 5, 'mm-cx-sel', { model: sel.model }, 'predictedComplexity') +
      '</div>' +
    '</div>' +
  '</div>'

  host.innerHTML = tilesHtml + detailHtml

  // Wire tile selection
  Array.prototype.forEach.call(host.querySelectorAll('.tile.clickable'), function (tile) {
    tile.onclick = function () {
      selectedModel = parseInt(tile.getAttribute('data-idx')!, 10)
      paintMix(rows)
    }
  })

  // Wire expand/collapse toggles and drill buttons
  Array.prototype.forEach.call(host.querySelectorAll('.mm-toggle[data-target]'), function (btn) {
    btn.onclick = function () {
      var tgt = document.getElementById(btn.getAttribute('data-target')!)
      if (!tgt) return
      var show = tgt.hidden
      tgt.hidden = !show
      btn.textContent = show ? 'show less' : btn.textContent
    }
  })
  wireDrillButtons(host)
}

function paintDimMix(
  rows: MixRow[], dim: 'taskType' | 'predictedComplexity', hostId: string,
  getSel: () => number, setSel: (v: number) => void,
) {
  var host = document.getElementById(hostId)
  if (!host) return
  var groups = aggregateByDim(rows, dim)
  if (!groups.length) { host.innerHTML = '<div class="empty">No data in this range.</div>'; return }

  var grandTotal = 0
  for (var i = 0; i < groups.length; i++) grandTotal += groups[i].cost

  var sel = getSel()
  if (sel >= groups.length) { sel = 0; setSel(0) }

  // Tiles
  var tiles = ''
  for (var i = 0; i < groups.length; i++) {
    var g = groups[i]
    var p = grandTotal > 0 ? Math.round((g.cost / grandTotal) * 100) : 0
    tiles += '<div class="tile clickable' + (i === sel ? ' on' : '') + '" data-idx="' + i + '">' +
      '<div class="label">' + esc(g.key) + '</div>' +
      '<div class="value"><span class="num">' + usd(g.cost) + '</span></div>' +
      '<div class="sub">' + p + '% of spend · ' + pct(g.succeeded, g.tasks) + ' success</div>' +
    '</div>'
  }

  // Table for selected tile
  var selected = groups[sel]
  var drillAll: DrillFilter = {}
  drillAll[dim] = selected.key

  var tbl = '<div class="mm-detail">' +
    '<div class="mm-detail-head">' + esc(selected.key) + ' — model breakdown</div>' +
    '<table class="sr-tbl"><thead><tr>' +
      '<th>Model</th>' +
      '<th class="num">Tasks</th>' +
      '<th class="num">Total Cost</th>' +
      '<th class="num">Success Rate</th>' +
      '<th class="num">Cost / Success</th>' +
      '<th></th>' +
    '</tr></thead><tbody>'

  for (var mi = 0; mi < selected.byModel.length; mi++) {
    var m = selected.byModel[mi]
    var filter = JSON.parse(JSON.stringify(drillAll))
    filter.model = m.model
    tbl += '<tr>' +
      '<td>' + esc(modelShort(m.model)) + '</td>' +
      '<td class="num">' + num(m.tasks) + '</td>' +
      '<td class="num">' + esc(usd(m.cost)) + '</td>' +
      '<td class="num">' + pct(m.succeeded, m.tasks) + '</td>' +
      '<td class="num">' + costPer(m.cost, m.succeeded) + '</td>' +
      '<td><span class="mm-drill" data-drill=\'' + esc(JSON.stringify(filter)) + '\' title="View tasks">View tasks</span></td>' +
    '</tr>'
  }

  tbl += '</tbody></table></div>'

  host.innerHTML = '<div class="tiles">' + tiles + '</div>' + tbl

  Array.prototype.forEach.call(host.querySelectorAll('.tile.clickable'), function (tile) {
    tile.onclick = function () {
      setSel(parseInt(tile.getAttribute('data-idx')!, 10))
      paintDimMix(rows, dim, hostId, getSel, setSel)
    }
  })
  wireDrillButtons(host)
}

function paintTimeBar() {
  var bar = document.getElementById('md-time-bar')
  if (!bar) return

  var timeBtns = MD_TIME_PRESETS.map(function (p) {
    return '<button data-d="' + p.d + '"' +
      (String(p.d) === String(mdDays) ? ' class="on"' : '') + '>' + p.l + '</button>'
  }).join('')

  bar.innerHTML =
    '<span class="fb-grp"><span class="fb-lbl">Time</span>' +
      '<div class="seg flt-seg" id="md-time">' + timeBtns + '</div>' +
    '</span>'

  Array.prototype.forEach.call(document.querySelectorAll('#md-time button'), function (btn) {
    btn.onclick = function () {
      var d = btn.getAttribute('data-d')
      mdDays = d === 'all' ? 'all' : parseInt(d, 10)
      Array.prototype.forEach.call(document.querySelectorAll('#md-time button'), function (b) {
        b.classList.toggle('on', b.getAttribute('data-d') === String(mdDays))
      })
      loaded = false
      lastKey = ''
      renderModels()
    }
  })
}

function paintBlockFilters() {
  var bar = document.getElementById('md-filters')
  if (!bar || !facets) return

  var taskOpts = '<option value="">All task types</option>' +
    facets.taskTypes.map(function (t) {
      return '<option value="' + esc(t) + '"' + (t === mdTaskType ? ' selected' : '') + '>' + esc(t) + '</option>'
    }).join('')

  var cxOpts = '<option value="">All complexities</option>' +
    facets.complexities.map(function (c) {
      return '<option value="' + esc(c) + '"' + (c === mdComplexity ? ' selected' : '') + '>' + esc(c) + '</option>'
    }).join('')

  var repoOpts = '<option value="">All repos</option>' +
    facets.repos.map(function (r) {
      return '<option value="' + esc(r) + '"' + (r === mdRepo ? ' selected' : '') + '>' + esc(r) + '</option>'
    }).join('')

  var successOpts = '<option value="">All outcomes</option>' +
    ['success', 'failure'].map(function (s) {
      return '<option value="' + esc(s) + '"' + (s === mdSuccess ? ' selected' : '') + '>' + esc(s) + '</option>'
    }).join('')

  var modelOpts = '<option value="">All models</option>' +
    facets.models.map(function (m) {
      return '<option value="' + esc(m) + '"' + (m === mdModel ? ' selected' : '') + '>' + esc(modelShort(m)) + '</option>'
    }).join('')

  bar.innerHTML =
    '<div class="flt-row">' +
      '<span class="flt-grp"><span class="flt-lbl">Task</span><select id="md-task">' + taskOpts + '</select></span>' +
      '<span class="flt-grp"><span class="flt-lbl">Complexity</span><select id="md-cx">' + cxOpts + '</select></span>' +
      '<span class="flt-grp"><span class="flt-lbl">Model</span><select id="md-model">' + modelOpts + '</select></span>' +
      '<span class="flt-grp"><span class="flt-lbl">Repo</span><select id="md-repo">' + repoOpts + '</select></span>' +
      '<span class="flt-grp"><span class="flt-lbl">Outcome</span><select id="md-success">' + successOpts + '</select></span>' +
      '<input id="md-q" class="flt-search" placeholder="search title / message" value="' + esc(mdSearch) + '" />' +
    '</div>'

  var searchTimer: any
  var qEl = document.getElementById('md-q') as HTMLInputElement | null
  if (qEl) qEl.oninput = function () {
    clearTimeout(searchTimer)
    searchTimer = setTimeout(function () { mdSearch = qEl!.value; fetchBlocks() }, 250)
  }

  wire('md-task', function (v) { mdTaskType = v })
  wire('md-cx', function (v) { mdComplexity = v })
  wire('md-model', function (v) { mdModel = v })
  wire('md-repo', function (v) { mdRepo = v })
  wire('md-success', function (v) { mdSuccess = v })
}

function wire(id: string, set: (v: string) => void) {
  var el = document.getElementById(id) as HTMLSelectElement | null
  // Read the element, not `this` — inside onchange, `this` types as
  // GlobalEventHandlers, which has no `value` (the client tsc gate fails on it).
  if (el) el.onchange = function () { set(el!.value); fetchBlocks() }
}

function clipLine(s: string, max: number): string {
  var line = s.split('\n')[0]
  return line.length > max ? line.slice(0, max - 3) + '…' : line
}

function blockMessage(b: any): string {
  var text = unquote(b.firstMessage) || b.firstPrompt
  return text ? clipLine(text, 120) : ''
}

function sessionTitle(b: any): string {
  return b.sessionTitle || b.firstPrompt?.split('\n')[0]?.slice(0, 80) || 'Untitled session'
}

function unquote(s: string | null): string {
  if (!s) return ''
  if (s.length >= 2 && s[0] === '"' && s[s.length - 1] === '"') return s.slice(1, -1)
  return s
}

function successBadge(s: string | null): string {
  var v = unquote(s) || 'unknown'
  if (v === 'success') return '<span class="md-badge md-badge-success">success</span>'
  if (v === 'partial') return '<span class="md-badge md-badge-partial">partial</span>'
  if (v === 'failure') return '<span class="md-badge md-badge-failure">failure</span>'
  return '<span class="md-badge md-badge-unknown">' + esc(v) + '</span>'
}

function fmtTok(n: number): string {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M'
  if (n >= 1_000) return (n / 1_000).toFixed(1) + 'K'
  return String(n || 0)
}

function repoShort(r: string | null): string {
  if (!r) return ''
  var parts = r.split('/')
  return parts[parts.length - 1] || r
}

interface SessionGroup {
  sessionId: string
  title: string
  repo: string
  cost: number
  blocks: any[]
}

function groupBySession(rows: any[]): SessionGroup[] {
  var map = new Map<string, SessionGroup>()
  var order: string[] = []
  for (var i = 0; i < rows.length; i++) {
    var b = rows[i]
    var sid = b.sessionId as string
    var g = map.get(sid)
    if (!g) {
      g = { sessionId: sid, title: sessionTitle(b), repo: repoShort(b.repo), cost: 0, blocks: [] }
      map.set(sid, g)
      order.push(sid)
    }
    g.cost += b.costUsd
    g.blocks.push(b)
  }
  var groups = order.map(function (sid) { return map.get(sid)! })
  for (var j = 0; j < groups.length; j++) {
    groups[j].blocks.sort(function (a, b) { return a.blockId - b.blockId })
  }
  return groups
}

function blockItemRow(b: any): string {
  var msg = blockMessage(b) || sessionTitle(b)
  return '<button class="md-item" data-sid="' + esc(b.sessionId) + '" data-rblk="' + b.blockId + '"' +
    ' data-agent="' + esc(b.agentId || 'main') + '">' +
    successBadge(b.success) +
    '<span class="md-item-msg">' + esc(msg) + '</span>' +
    '<span class="md-chip">' + esc(b.taskType || '?') + '</span>' +
    '<span class="md-chip">' + esc(b.predictedComplexity || '?') + '</span>' +
    '<span class="md-item-turns">' + num(b.userTurns) + ' turns</span>' +
    '<span class="md-item-cost">' + usd(b.costUsd) + '</span>' +
  '</button>'
}

function paintRoster() {
  var host = document.getElementById('md-roster')
  if (!host) return
  var rows = blocksData || []
  if (!rows.length) {
    host.innerHTML = '<div class="empty">No tasks match this view.</div>'
    return
  }

  var groups = groupBySession(rows)
  var html = ''
  for (var i = 0; i < groups.length; i++) {
    var g = groups[i]
    html += '<div class="th-grp">' +
      '<button type="button" class="th-grp-head" data-sid="' + esc(g.sessionId) + '">' +
        '<span class="th-grp-caret">›</span>' +
        '<span class="th-grp-title">' + esc(g.title) + '</span>' +
        (g.repo ? '<span class="md-chip md-chip-repo">' + esc(g.repo) + '</span>' : '') +
        '<span class="th-grp-n">' + usd(g.cost) + '</span>' +
        '<span class="th-grp-n">' + num(g.blocks.length) + (g.blocks.length === 1 ? ' task' : ' tasks') + '</span>' +
      '</button>' +
      '<div class="th-grp-items" hidden>' +
        g.blocks.map(blockItemRow).join('') +
      '</div>' +
    '</div>'
  }

  host.innerHTML = html

  function focusFor(el) {
    var rblk = el.getAttribute('data-rblk')
    var agent = el.getAttribute('data-agent')
    if (rblk == null) return undefined
    var f: any = { routableBlockId: parseInt(rblk, 10) }
    if (agent && agent !== 'main') f.agentId = agent
    return f
  }

  Array.prototype.forEach.call(host.querySelectorAll('.th-grp-head'), function (head) {
    head.onclick = function () {
      var items = this.parentNode!.querySelector('.th-grp-items')! as HTMLElement
      var opening = items.hidden
      items.hidden = !opening
      this.classList.toggle('open', opening)
    }
  })

  Array.prototype.forEach.call(host.querySelectorAll('.md-item'), function (el) {
    el.onclick = function (e: Event) {
      e.stopPropagation()
      openDetail(this.getAttribute('data-sid')!, focusFor(this))
    }
  })
}
